import React, {useEffect, useState} from 'react';
import {noop} from 'lodash';
import {TypeImplementation, Types} from '@packages/systems/wfdl';
import {
  type ContextualExpression,
  ContextualExpressions,
  stripAnn,
  getAnn,
} from '@packages/utilities/contextual-expression';
import {TTypeReference} from '@packages/systems/wfdl-ast';
import {selectComponents} from '@packages/utilities/platform-selectors';
import {findAccessGroupsInComponentMap} from '@packages/systems/users/usysWfdl';
import {AccessGroupMaxLimit} from './AccessGroupMaxLimit';
import {
  Select,
  Option,
  Group,
} from '@packages/systems/design-system/WebflowSelect';
import TooltipTrigger from '@packages/systems/design-system/TooltipTrigger';
import {weakMemo} from '@packages/utilities/memo';
import {useDesignerSelector} from '@packages/systems/designer/flux';

type AccessGroup = {
  name: string;
  shortId: string;
};

const getAccessGroupsAsArray = weakMemo(
  (expr: ContextualExpression): AccessGroup[] => {
    const accessGroups: Array<AccessGroup> = [];

    const {type} = getAnn(expr);
    if (!type) {
      return accessGroups;
    }

    for (const [shortId, name] of Object.entries(
      Types.getEnumValues(type) || {}
    )) {
      accessGroups.push({
        shortId,
        name,
      });
    }

    return accessGroups;
  }
);

const AccessGroupSelectType = TypeImplementation.Alias({
  params: ['T'],
  type: TTypeReference('T'),
  render: function AccessGroupSelectRender({expr}) {
    const accessGroups = getAccessGroupsAsArray(expr);
    const componentMap = useDesignerSelector(selectComponents);
    const [usedAccessGroupsShortIds, setUsedAccessGroupsShortIds] =
      useState<Set<string> | null>(null);

    const [usedAccessGroupNames, setUserAccessGroupNames] = useState([]);
    const currentShortId = ContextualExpressions.getEnum(stripAnn(expr)) || '';

    useEffect(() => {
      if (usedAccessGroupsShortIds === null) {
        setUsedAccessGroupsShortIds(
          findAccessGroupsInComponentMap(componentMap)
        );
      }
    }, [componentMap, usedAccessGroupsShortIds]);

    useEffect(() => {
      setUserAccessGroupNames(
        // @ts-expect-error - TS2345 - Argument of type 'any[]' is not assignable to parameter of type 'SetStateAction<never[]>'.
        accessGroups.reduce<Array<any>>((acc, {name, shortId}) => {
          if (
            (usedAccessGroupsShortIds !== null &&
              usedAccessGroupsShortIds.has(shortId)) ||
            shortId === currentShortId
          ) {
            acc.push(name);
          }
          return acc;
        }, [])
      );
    }, [currentShortId, usedAccessGroupsShortIds, accessGroups]);

    return (
      <>
        <Select
          id="AccessGroup-Option"
          data-automation-id="AccessGroup-Option"
          value={currentShortId}
          disabled
          placeholder="Select access group..."
          onChange={noop}
        >
          <Group>
            {accessGroups.map((value) => {
              return (
                <Option
                  key={value.shortId}
                  value={value.shortId}
                  aria-labelledby="AccessGroup-Option"
                  label={
                    <TooltipTrigger alternate content={value.name}>
                      <span>{value.name}</span>
                    </TooltipTrigger>
                  }
                />
              );
            })}
          </Group>
        </Select>
        <AccessGroupMaxLimit accessGroupNames={usedAccessGroupNames} />
      </>
    );
  },
});

export default AccessGroupSelectType;
